import { useState, useEffect } from 'react';
import { Dispatch, SetStateAction } from 'react';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import { Modal, Button, } from '@mui/material'
import { useCreateFaqMutation, useUpdateFaqMutation, FaqItem } from '../store/api/manageAPI';
import { showToast, showError } from '../constants/toasts';
import AppLoader from '../constants/Loader';

interface ModalProps {
    open: boolean;
    onClose: () => void;
    setOpen: Dispatch<SetStateAction<boolean>>;
    isEdit?: boolean;
    data?: FaqItem | null;
}

export default function AddFaq({ open, onClose, setOpen, isEdit = false, data = null }: ModalProps) {
    const [question, setQuestion] = useState("");
    const [answer, setAnswer] = useState("");
    const [error, setError] = useState(false);

    const [createFaq, { isLoading: isCreating }] = useCreateFaqMutation();
    const [updateFaq, { isLoading: isUpdating }] = useUpdateFaqMutation();

    useEffect(() => {
        if (open) {
            if (isEdit && data) {
                setQuestion(data.question || "");
                setAnswer(data.answer || "");
            } else {
                setQuestion("");
                setAnswer("");
            }
            setError(false);
        }
    }, [open, isEdit, data]);

    const handleSubmit = async () => {
        if (!question.trim() || !answer.trim()) {
            setError(true);
            return;
        }

        if (question.length > 200) {
            showError("Question must be less than 200 characters");
            return;
        }

        if (answer.length > 500) {
            showError("Answer must be less than 500 characters");
            return;
        }

        try {
            if (isEdit && data?._id) {
                const response = await updateFaq({ id: data._id, question, answer }).unwrap();
                showToast(response?.message || "FAQ updated successfully");
            } else {
                const response = await createFaq({ question, answer }).unwrap();
                showToast(response?.message || "FAQ added successfully");
            }
            setOpen(false);
            onClose();
        } catch (err: any) {
            console.error("Error saving FAQ:", err);
            const msg = err?.data?.message || err?.message || "Something went wrong";
            showError(msg);
        }
    };

    return (
        <Modal
            className="modal addNotification_modal"
            open={open}
            onClose={() => {
                onClose();
                setError(false);
            }}
        >
            <div className="modal-dialog">
                {(isCreating || isUpdating) && <AppLoader isLoad={true} />}
                <div className="modal-body">
                    <div className="modal_title">
                        <h2>{isEdit ? "Edit FAQ" : "Add FAQ"}</h2>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setOpen(false)}
                        >
                            <CloseIcon />
                        </button>
                    </div>
                    <form className="form">
                        <div className="control_group">
                            <label>Question</label>
                            <TextField
                                placeholder="Enter Question"
                                fullWidth
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                hiddenLabel
                                inputProps={{ maxLength: 200 }}
                            />
                            {error && !question.trim() && (
                                <h6 className="err_msg">Question is required</h6>
                            )}

                        </div>
                        <div className="control_group">
                            <label>Answer</label>
                            <TextField
                                placeholder="Enter Answer"
                                fullWidth
                                hiddenLabel
                                multiline
                                value={answer}
                                onChange={(e) => setAnswer(e.target.value)}
                                maxRows={5}
                                minRows={5}
                                inputProps={{ maxLength: 500 }}
                            />
                            {error && !answer.trim() && (
                                <h6 className="err_msg">Answer is required</h6>
                            )}

                        </div>
                        <div className="footer_btn_flex">
                            <Button className="btn btn_grey" onClick={() => {
                                setError(false);
                                setOpen(false);
                            }}>Cancel</Button>
                            <Button className="btn btn_primary" onClick={handleSubmit}>
                                Submit
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </Modal>
    )
}
